// ===== SISTEMA DE TRIAJE DOCUMENTAL CON 3 NIVELES =====
// Nivel 1: Clasificación automática simple
// Nivel 2: Requiere IA profunda (Document AI)
// Nivel 3: Revisión humana manual

/**
 * Detecta el nivel de complejidad del documento
 */
export function getDocumentComplexity(driveFile) {
    const filename = (driveFile.name || driveFile.driveFileName || '').toLowerCase();
    const fileExt = filename.split('.').pop();

    // NIVEL 1: FÁCIL - Clasificación automática simple
    const easyPatterns = {
        // Correos electrónicos
        email: /\.(eml|msg)$/i,
        emailKeywords: /(email|correo|mail)/i,

        // Contratos (con palabras clave claras)
        contract: /(contrato|contract|convenio|agreement)/i,

        // Documentos legales estándar
        legalDocs: /(demanda|sentencia|resolucion|notificacion)/i,

        // PDFs bien nombrados con fecha
        datedPdf: /\d{4}[-_]\d{2}[-_]\d{2}.*\.pdf$/i
    };

    // NIVEL 3: DIFÍCIL - Requiere intervención
    const hardPatterns = {
        // Imágenes escaneadas
        scannedImages: /\.(jpg|jpeg|png|gif|bmp|tiff)$/i,

        // Archivos sin extensión clara
        noExtension: /^[^.]+$/,

        // Nombres genéricos
        genericNames: /^(imagen|foto|scan|escaneo|documento|doc|sin.?titulo|untitled|archivo)/i,

        // Muy cortos (probablemente mal nombrados)
        tooShort: filename.length < 10
    };

    // Clasificar complejidad
    if (easyPatterns.email.test(filename) || easyPatterns.emailKeywords.test(filename)) {
        return { level: 1, type: 'email', reason: 'Correo electrónico detectado' };
    }

    if (easyPatterns.contract.test(filename)) {
        return { level: 1, type: 'contract', reason: 'Contrato detectado por nombre' };
    }

    if (easyPatterns.legalDocs.test(filename)) {
        return { level: 1, type: 'legal', reason: 'Documento legal estándar' };
    }

    if (easyPatterns.datedPdf.test(filename)) {
        return { level: 1, type: 'dated_pdf', reason: 'PDF con fecha en nombre' };
    }

    // "TOTAL FREEDOM": Todo lo que antes era difícil (Nivel 3), ahora va a la IA (Nivel 2)

    if (hardPatterns.scannedImages.test(filename)) {
        return { level: 2, type: 'scanned_image', reason: 'Imagen escaneada - Enviando a Gemini Vision' };
    }

    // NIVEL 2: MEDIO - Requiere IA pero es factible
    if (fileExt === 'pdf') {
        return { level: 2, type: 'standard_pdf', reason: 'PDF estándar - IA extraerá detalles' };
    }

    // Nombres genéricos - Intentar con IA de todas formas
    if (hardPatterns.genericNames.test(filename)) {
        return { level: 2, type: 'generic', reason: 'Nombre genérico - IA intentará identificar' };
    }

    if (hardPatterns.tooShort) {
        return { level: 2, type: 'unnamed', reason: 'Nombre corto - IA analizará contenido' };
    }

    // Verificar si realmente no tiene extensión
    if (!filename.includes('.') || fileExt.length > 5) {
        return { level: 2, type: 'no_extension', reason: 'Sin extensión - IA intentará detectar tipo' };
    }

    // Por defecto, todo a IA
    return { level: 2, type: 'standard', reason: 'Documento estándar - Requiere análisis IA' };
}

/**
 * Detecta duplicados por hash de nombre de archivo y tamaño
 */
export function detectDuplicates(driveFiles) {
    const seen = new Map();
    const duplicates = [];

    driveFiles.forEach((file, index) => {
        // Normalizar nombre (sin espacios, lowercase)
        const normalizedName = (file.name || '').toLowerCase().replace(/\s+/g, '');
        const size = file.size || 0;
        const hash = `${normalizedName}_${size}`;

        if (seen.has(hash)) {
            duplicates.push({
                index,
                file,
                originalIndex: seen.get(hash),
                hash
            });
        } else {
            seen.set(hash, index);
        }
    });

    return duplicates;
}

/**
 * Detecta duplicados por similitud de contenido (nombre similar)
 */
export function detectSimilarDocuments(driveFiles) {
    const similar = [];

    for (let i = 0; i < driveFiles.length; i++) {
        for (let j = i + 1; j < driveFiles.length; j++) {
            const name1 = (driveFiles[i].name || '').toLowerCase();
            const name2 = (driveFiles[j].name || '').toLowerCase();

            // Calcular similitud (Levenshtein simplificado)
            const similarity = calculateSimilarity(name1, name2);

            if (similarity > 0.8) { // 80% similar
                similar.push({
                    file1: driveFiles[i],
                    file2: driveFiles[j],
                    similarity: Math.round(similarity * 100),
                    index1: i,
                    index2: j
                });
            }
        }
    }

    return similar;
}

/**
 * Calcula similitud entre dos strings (0-1)
 */
function calculateSimilarity(str1, str2) {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;

    if (longer.length === 0) return 1.0;

    const editDistance = levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
}

/**
 * Distancia de Levenshtein (número de cambios necesarios)
 */
function levenshteinDistance(str1, str2) {
    const matrix = [];

    for (let i = 0; i <= str2.length; i++) {
        matrix[i] = [i];
    }

    for (let j = 0; j <= str1.length; j++) {
        matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
        for (let j = 1; j <= str1.length; j++) {
            if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
                matrix[i][j] = matrix[i - 1][j - 1];
            } else {
                matrix[i][j] = Math.min(
                    matrix[i - 1][j - 1] + 1,
                    matrix[i][j - 1] + 1,
                    matrix[i - 1][j] + 1
                );
            }
        }
    }

    return matrix[str2.length][str1.length];
}

/**
 * Analiza documento con sistema de triaje
 */
export function analyzeDocumentWithTriage(driveFile) {
    const complexity = getDocumentComplexity(driveFile);
    const filename = driveFile.name || driveFile.driveFileName || '';

    let status = 'pending';
    let color = '#60a5fa'; // azul por defecto
    let action = 'process'; // Por defecto: procesar

    // Determinar status y color según nivel
    if (complexity.level === 1) {
        // VERDE: Procesar automáticamente
        status = 'auto_classify';
        color = '#10b981';
        action = 'auto';
    } else if (complexity.level === 2) {
        // AMARILLO: Requiere IA
        status = 'needs_ai';
        color = '#facc15';
        action = 'ai_classify';
    } else {
        // ROJO: Requiere humano
        status = 'needs_human';
        color = '#ef4444';
        action = 'manual_review';
    }

    return {
        ...driveFile,
        complexity,
        status,
        color,
        action,
        triageLevel: complexity.level,
        triageReason: complexity.reason
    };
}

/**
 * Procesa batch con triaje y detección de duplicados
 */
export function processBatchWithTriage(driveFiles) {
    // Analizar cada documento
    const analyzed = driveFiles.map(file => analyzeDocumentWithTriage(file));

    // Detectar duplicados exactos
    const exactDuplicates = detectDuplicates(driveFiles);

    // Detectar documentos similares
    const similar = detectSimilarDocuments(driveFiles);

    // Marcar duplicados
    exactDuplicates.forEach(dup => {
        analyzed[dup.index].isDuplicate = true;
        analyzed[dup.index].duplicateOf = dup.originalIndex;
        analyzed[dup.index].status = 'duplicate';
        analyzed[dup.index].color = '#dc2626'; // rojo oscuro
        analyzed[dup.index].action = 'delete';
    });

    // Marcar similares (para revisión)
    similar.forEach(sim => {
        if (!analyzed[sim.index2].isDuplicate) {
            analyzed[sim.index2].isPossibleDuplicate = true;
            analyzed[sim.index2].similarTo = sim.index1;
            analyzed[sim.index2].similarity = sim.similarity;
        }
    });

    // Separar por nivel de triaje
    const byLevel = {
        level1_auto: analyzed.filter(d => d.triageLevel === 1 && !d.isDuplicate),
        level2_ai: analyzed.filter(d => d.triageLevel === 2 && !d.isDuplicate),
        level3_human: analyzed.filter(d => d.triageLevel === 3 && !d.isDuplicate),
        duplicates: analyzed.filter(d => d.isDuplicate),
        possibleDuplicates: analyzed.filter(d => d.isPossibleDuplicate && !d.isDuplicate)
    };

    return {
        all: analyzed,
        byLevel,
        stats: {
            total: analyzed.length,
            autoClassify: byLevel.level1_auto.length,
            needsAI: byLevel.level2_ai.length,
            needsHuman: byLevel.level3_human.length,
            duplicates: byLevel.duplicates.length,
            possibleDuplicates: byLevel.possibleDuplicates.length
        },
        duplicates: {
            exact: exactDuplicates,
            similar: similar
        }
    };
}

/**
 * Genera reporte visual del triaje
 */
export function generateTriageReport(triageResult) {
    const { stats } = triageResult;

    return `
📊 ANÁLISIS DE DOCUMENTOS - SISTEMA DE TRIAJE

📈 Resumen General:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  Total de documentos: ${stats.total}

🟢 NIVEL 1 - Clasificación Automática: ${stats.autoClassify}
   ↳ Correos, contratos y docs con metadata clara
   ↳ Se procesarán automáticamente

🟡 NIVEL 2 - Requiere IA: ${stats.needsAI}
   ↳ Documentos estándar sin metadata obvia
   ↳ Se analizarán con Document AI

🔴 NIVEL 3 - Revisión Humana: ${stats.needsHuman}
   ↳ Imágenes, escaneados de mala calidad
   ↳ Requieren clasificación manual

🗑️ DUPLICADOS DETECTADOS:
   Exactos: ${stats.duplicates}
   Posibles duplicados: ${stats.possibleDuplicates}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  `.trim();
}

/**
 * Obtiene ícono y emoji según nivel de triaje
 */
export function getTriageIcon(level) {
    const icons = {
        1: '🟢',
        2: '🟡',
        3: '🔴'
    };
    return icons[level] || '⚪';
}

/**
 * Obtiene label de acción según status
 */
export function getActionLabel(action) {
    const labels = {
        'auto': 'Procesar Automáticamente',
        'ai_classify': 'Analizar con IA',
        'manual_review': 'Revisión Humana Requerida',
        'delete': 'Duplicado - Marcar para Eliminar'
    };
    return labels[action] || 'Procesar';
}
